import { Control } from "Controls/Control";
import { ControlEventTypes } from "Core/Events/EventTypes";

/**
 * Control for editing html code.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Access the instance of a HtmlEditor with ControlID 1337
 * // In script:
 * @@ctr:1337@@
 *
 * // On page:
 * $dms(1337)
 * ```
 */
export class HtmlEditor extends Control {
    private _kendoElement: kendo.ui.Editor;

    /**
     * @hidden
     */
    protected defaultEditorElement: string = "<textarea/>";

    /**
     * @hidden
     */
    protected dmsControlClass: string = "dms-control-htmleditor";

    /**
     * Gets the outer element. This is either the HTMLElement or a wrapper added by the control.
     * @since 4.0.0
     */
    public getOuterElement(): JQuery {
        return this._kendoElement.wrapper;
    }

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     * @hidden
     */
    protected setElementInternal(element: JQuery): void {
        this.jQueryElement.on("focusout", (e) => this.trigger(ControlEventTypes.LostFocus));

        this._kendoElement = new kendo.ui.Editor(this.jQueryElement[0], {
            change: (e) => this.trigger(ControlEventTypes.Change),
            resizable: {
                content: true,
                toolbar: true,
            },
            tools: [
                { name: "viewHtml" },
                { name: "bold" },
                { name: "italic" },
                { name: "underline" },
                { name: "strikethrough" },
                { name: "justifyLeft" },
                { name: "justifyCenter" },
                { name: "justifyRight" },
                { name: "justifyFull" },
                { name: "formatting" },
                { name: "cleanFormatting" },
                { name: "fontName" },
                { name: "fontSize" },
                { name: "foreColor" },
                { name: "backColor" },
                { name: "insertUnorderedList" },
                { name: "insertOrderedList" },
                { name: "indent" },
                { name: "outdent" },
                { name: "createLink" },
                { name: "unlink" },
                { name: "insertImage" },
                { name: "insertFile" },
                { name: "subscript" },
                { name: "superscript" },
                { name: "tableWizard" },
                { name: "createTable" },
                { name: "addRowAbove" },
                { name: "addRowBelow" },
                { name: "addColumnLeft" },
                { name: "addColumnRight" },
                { name: "deleteRow" },
                { name: "deleteColumn" },
                { name: "print" },
                { name: "pdf" },
            ],
        });
        setTimeout(() => {
            // fix for not resizing the editor properly
            this.jQueryElement.resize();
        });
    }

    /**
     * Get the kendo editor control. Click {@link http://docs.telerik.com/kendo-ui/api/javascript/ui/editor|here} for the api definition.
     * @since 4.0.0
     */
    public getKendoElement(): kendo.ui.Editor {
        return this._kendoElement;
    }

    /**
     * Set the control value.
     * @param value Html code to be set.
     * @since 4.0.0
     */
    public setValue(value: string): void {
        let oldValue = this.getValue();
        if (oldValue !== value) {
            this._kendoElement.value(value);
            this._kendoElement.trigger("change");
        }
    }

    /**
     * Get the control value as html string.
     * @since 4.0.0
     */
    public getValue(): string {
        return this._kendoElement.value();
    }

    /**
     * Enable the control.
     * @since 4.0.0
     */
    public enable(): void {
        $(this._kendoElement.body).prop("contenteditable", true);
    }

    /**
     * Disable the control.
     * @since 4.0.0
     */
    public disable(): void {
        $(this._kendoElement.body).prop("contenteditable", false);
    }

    /**
     * Returns true if the control is enabled.
     * @since 4.0.0
     */
    public isEnabled(): boolean {
        return $(this._kendoElement.body).prop("contenteditable") == "true";
    }

    /**
     * Gets template for showing tooltips.
     * @param bindingField Bound field in the datarow.
     * @hidden
     */
    protected getTooltipTemplate(bindingField?: string): string {
        return ``;
    }

    /**
     * Gets the display template for readonly columns and non-editable grid.
     * @hidden
     */
    protected getDisplayTemplate(bindingField?: string): string {
        if (this.controlDefinition.EncodeHtml) {
            return `#= ${bindingField} == null ? "${
                this.controlDefinition.ReplaceNullValue || ""
            }" : DMS.Util.encode(DMS.Util.convertToString(${bindingField}), DMS.Enums.EncodingTypes.Html)#`;
        } else {
            return `#= ${bindingField} == null ? "${
                this.controlDefinition.ReplaceNullValue || ""
            }" : DMS.Util.convertToString(${bindingField})#`;
        }
    }

    /**
     * Dispose the control and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();
        if (this._kendoElement != null) {
            this._kendoElement.destroy();
        }
    }
}
